import { Link } from "react-router-dom";
import { WHITEPAPER_URL, whitepaperChapters } from "../content";
import {
  NetworkGraph,
  PolicyDiagram,
  ReceiptCard,
  TrustLayerDiagram,
} from "../components/Diagrams";
import {
  DataCards,
  LinkCards,
  PageHero,
  PageSection,
  StepList,
} from "./HomePage";

export function GatewayPage() {
  return (
    <>
      <PageHero
        eyebrow="ORIQ AGENT TRUST GATEWAY"
        title="The control layer between autonomous agents and external execution."
        body="ORIQ intercepts consequential agent actions, resolves authority, validates capabilities, evaluates policy, controls execution, and generates cryptographic proofs alongside inspectable records."
      />
      <PageSection
        eyebrow="01 / GATEWAY"
        title="A boundary at the point of action"
        body="The gateway turns a broad request into a controlled Action Session with a clear source, purpose, authority, capability, decision, execution record, and recovery path."
      >
        <div className="architecture-band">
          <div className="architecture-step">
            <span>AGENT PLANE</span>
            <strong>Action Request</strong>
            <small>Principal · Intent · Context</small>
          </div>
          <div className="architecture-step architecture-focus">
            <span>ORIQ CONTROL PLANE</span>
            <strong>Trust Gateway</strong>
            <small>Identity · Authority · Capability · Policy</small>
          </div>
          <div className="architecture-step">
            <span>EXECUTION PLANE</span>
            <strong>External Action</strong>
            <small>Provider · Tool · Outcome</small>
          </div>
        </div>
      </PageSection>
      <PageSection
        eyebrow="02 / CONTROL FLOW"
        title="From request to verified outcome"
      >
        <StepList
          items={[
            [
              "Agent Action Request",
              "Capture the requested action, the acting agent, and the execution context.",
            ],
            [
              "Identity / Intent / Authority",
              "Resolve who the agent represents, why it is acting, and what was delegated.",
            ],
            [
              "Capability Validation",
              "Issue or validate a purpose-specific capability with bounded action, resource, amount, time, and counterparty.",
            ],
            [
              "Policy Engine",
              "Evaluate the action against rules and choose Allow, Deny, Review, Pause, or Compensate.",
            ],
            [
              "Execution Control",
              "Permit only the qualified action to reach the external system.",
            ],
            [
              "Verification",
              "Compare expected and observed outcomes, then produce an Action Receipt.",
            ],
            [
              "Recovery",
              "Revoke, reverse, compensate, restore, or notify when execution or its records are not acceptable.",
            ],
          ]}
        />
      </PageSection>
      <PageSection
        eyebrow="03 / POLICY ENGINE"
        title="Every consequential action passes through policy."
        id="policy-engine"
      >
        <PolicyDiagram />
      </PageSection>
      <PageSection
        eyebrow="04 / INTEGRATION"
        title="Protocol-neutral by design"
        body="ORIQ connects the identity providers, APIs, MCP servers, key management, payment rails, cloud infrastructure, and blockchains already present in an organization. It does not replace them."
      >
        <div className="integration-line">
          <span>Agent Runtime</span>
          <i>→</i>
          <strong>ORIQ</strong>
          <i>→</i>
          <span>External Infrastructure</span>
        </div>
      </PageSection>
    </>
  );
}

export function DelegatedPage() {
  return (
    <>
      <PageHero
        eyebrow="DELEGATED ACTION CONTROL"
        title="Bounded authority for autonomous agents."
        body="Agents need capabilities, not keys. ORIQ converts principal authority into scoped action rights that can be constrained, monitored, revoked, and verified."
      />
      <PageSection
        eyebrow="01 / THE AUTHORITY PROBLEM"
        title="Credentials prove access. They do not define intent."
        body="A credential can open a door without explaining who authorized the action, what the agent was asked to accomplish, or how far the authority may extend."
      >
        <div className="quote-panel">
          “The authority available to an agent must be narrower than the
          authority held by the principal who delegated it.”
        </div>
      </PageSection>
      <PageSection
        eyebrow="02 / DELEGATION MODEL"
        title="Principal → Agent → Sub-Agent"
      >
        <div className="delegation-graph">
          {[
            "Principal",
            "Planning Agent",
            "Execution Agent",
            "Tool Agent",
            "Provider",
          ].map((item, index) => (
            <div key={item}>
              <span>0{index + 1}</span>
              <strong>{item}</strong>
              {index < 4 && <i>↓</i>}
            </div>
          ))}
        </div>
      </PageSection>
      <PageSection eyebrow="03 / CONSTRAINTS" title="A capability has a shape">
        <DataCards
          items={[
            ["Action", "The operation the agent may perform."],
            ["Resource", "The system, object, category, or service in scope."],
            ["Amount", "A bounded budget, quantity, or risk limit."],
            [
              "Counterparty",
              "Approved providers, recipients, or destinations.",
            ],
            ["Time", "A session, expiry, or execution window."],
            ["Delegation", "Whether and how authority may be passed onward."],
          ]}
        />
      </PageSection>
      <PageSection eyebrow="04 / RECOVERY" title="Authority is not permanent">
        <div className="state-track">
          <span>ACTIVE</span>
          <i>→</i>
          <span>RESTRICTED</span>
          <i>→</i>
          <span>SUSPENDED</span>
          <i>→</i>
          <span className="state-danger">REVOKED</span>
        </div>
        <p className="body-narrow">
          A capability can be paused or revoked without changing the principal's
          underlying authority. Recovery is part of the action-control path.
        </p>
      </PageSection>
    </>
  );
}

export function ReceiptsPage() {
  return (
    <>
      <PageHero
        eyebrow="ACTION RECEIPTS"
        title="Cryptographic proof for autonomous execution."
        body="An Action Receipt connects the principal intent, delegated authority, policy decision, execution events, outcome, and responsibility chain in a form people can inspect."
      />
      <PageSection
        eyebrow="01 / WHY EXECUTION NEEDS PROOF"
        title="A log records events. A receipt explains responsibility."
        body="Execution Success is not the same as Outcome Success. Settlement Proof is not the same as Fulfillment Proof. ORIQ keeps those distinctions visible."
      >
        <ReceiptCard />
      </PageSection>
      <PageSection
        eyebrow="02 / ACTION VERIFICATION"
        title="Expected state → execution → observed state → verification result"
      >
        <div className="verification-track">
          <span>EXPECTED STATE</span>
          <i>↓</i>
          <strong>EXECUTION</strong>
          <i>↓</i>
          <span>OBSERVED STATE</span>
          <i>↓</i>
          <b>VERIFICATION RESULT</b>
        </div>
      </PageSection>
      <PageSection
        eyebrow="03 / RECEIPT & PROOF"
        title="The receipt carries the proof"
      >
        <DataCards
          items={[
            [
              "Authorization Proof",
              "Who delegated the action and under which authority profile.",
            ],
            [
              "Execution Proof",
              "What tool, parameters, policy decision, and execution event occurred.",
            ],
            [
              "Outcome Proof",
              "Whether the external result matched the expected state.",
            ],
            [
              "Accountability",
              "Which principal, agent, provider, verifier, and recovery path are responsible.",
            ],
          ]}
        />
      </PageSection>
      <PageSection
        eyebrow="04 / CRYPTOGRAPHIC PROOF"
        title="Cryptographic proof that can be checked"
      >
        <div className="hash-list">
          {[
            "Intent Hash",
            "Authority Proof",
            "Capability Proof",
            "Execution Proof",
            "Outcome Attestation",
            "Receipt Hash",
          ].map((item, index) => (
            <div key={item}>
              <span>0{index + 1}</span>
              <strong>{item}</strong>
              <code>
                {
                  [
                    "intent_7f2a...",
                    "authority_91ac...",
                    "capability_02ef...",
                    "execution_2d10...",
                    "outcome_4b12...",
                    "receipt_c14e...",
                  ][index]
                }
              </code>
            </div>
          ))}
        </div>
      </PageSection>
    </>
  );
}

export function ProtocolPage() {
  return (
    <>
      <PageHero
        eyebrow="ORIQ PROTOCOL"
        title="A trust protocol for autonomous execution."
        body="ORIQ defines the relationship between Principal Intent, delegated authority, agent capabilities, external actions, verified outcomes, and recovery."
      />
      <PageSection
        eyebrow="01 / PROTOCOL THESIS"
        title="Permission. Proof. Accountability."
        body="The protocol places control before external consequence and preserves inspectable records and cryptographic proof after the action."
      >
        <div className="protocol-pillars">
          <span>Permission</span>
          <span>Proof</span>
          <span>Accountability</span>
        </div>
      </PageSection>
      <PageSection
        eyebrow="02 / PROTOCOL FLOW"
        title="Intent → Authority → Capability → Policy → Action → Verification"
      >
        <TrustLayerDiagram />
      </PageSection>
      <PageSection
        eyebrow="03 / ARCHITECTURE"
        title="Four planes, one action context"
        id="architecture"
      >
        <DataCards
          items={[
            ["Agent Plane", "Reasoning, planning, and action requests."],
            [
              "ORIQ Control Plane",
              "Identity, intent, authority, capability, policy, and recovery.",
            ],
            [
              "Execution Plane",
              "External tools, providers, cloud, APIs, MCP, and payment rails.",
            ],
            [
              "Proof & Record Plane",
              "Cryptographic proofs, receipts, attestations, commitments, and accountability.",
            ],
          ]}
        />
      </PageSection>
      <PageSection
        eyebrow="04 / IDENTITY & INTENT"
        title="Know who acts and why"
        id="identity-intent"
      >
        <StepList
          items={[
            [
              "KYA Profile",
              "Establish the principal, owner, agent provenance, model, and runtime context.",
            ],
            [
              "Intent Manifest",
              "Represent the original task, structured goal, constraints, confirmation, and version.",
            ],
            [
              "Delegated Authority",
              "Bind the intent to rules, capability scope, expiry, and recovery.",
            ],
            [
              "Qualified Action",
              "Allow execution only when the necessary facts align.",
            ],
          ]}
        />
      </PageSection>
      <PageSection
        eyebrow="05 / POLICY & RECOVERY"
        title="Decisions remain explainable"
      >
        <div className="split-layout">
          <PolicyDiagram />
          <div className="decision-copy">
            <p>
              Every Deny, Review, Pause, Revoke, or Compensate outcome must
              expose the rule, proof gap, affected object, and next action.
            </p>
            <Link className="arrow-link" to="/security">
              Read the Security model <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </PageSection>
    </>
  );
}

export function NetworkPage() {
  return (
    <>
      <PageHero
        eyebrow="ORIQ NETWORK"
        title="A shared trust environment for autonomous agents."
        body="The network extends trusted action control into shared verification across independent trust domains."
        status="NETWORK ARCHITECTURE — IN DEVELOPMENT"
      />
      <PageSection
        eyebrow="01 / NETWORK ROLES"
        title="A progressive trust environment"
      >
        <NetworkGraph />
        <div className="role-grid">
          <DataCards
            items={[
              ["Principal", "Authority origin."],
              ["Agent", "Autonomous execution."],
              ["Provider", "External service or resource."],
              ["Verifier", "Outcome verification."],
              ["Validator", "Shared network state validation."],
            ]}
          />
        </div>
      </PageSection>
      <PageSection
        eyebrow="02 / VERIFICATION"
        title="Action Receipt → Verifier → Validator → Shared Trust"
        id="verifiers"
      >
        <div className="verification-chain">
          <span>Action Receipt</span>
          <i>→</i>
          <span>Verifier</span>
          <i>→</i>
          <span>Validator</span>
          <i>→</i>
          <strong>Shared Trust</strong>
        </div>
      </PageSection>
      <PageSection
        eyebrow="03 / PROGRESSIVE DECENTRALIZATION"
        title="From enterprise to federated to public"
      >
        <div className="decentralization">
          <span>Enterprise</span>
          <i>→</i>
          <span>Federated</span>
          <i>→</i>
          <strong>Public Network</strong>
        </div>
      </PageSection>
      <PageSection
        eyebrow="04 / ECONOMIC SECURITY"
        title="Utility remains bounded by the protocol"
      >
        <p className="body-narrow">
          ORIQ Token is described only as a future network utility for staking,
          economic security, verifier bonds, validator incentives, and
          governance. Parameters remain TBD.
        </p>
        <div className="tbd-grid">
          <span>SYMBOL — TBD</span>
          <span>SUPPLY — TBD</span>
          <span>REWARD RATE — TBD</span>
          <span>GOVERNANCE — TBD</span>
        </div>
      </PageSection>
    </>
  );
}

export function SecurityPage() {
  return (
    <>
      <PageHero
        eyebrow="SECURITY"
        title="Security for systems that can act."
        body="ORIQ assumes agents, credentials, gateways, providers, verifiers, validators, and external systems may fail or become compromised."
      />
      <PageSection
        eyebrow="01 / SECURITY LAYERS"
        title="Fail closed at every boundary"
      >
        <div className="security-stack">
          {[
            "Identity Security",
            "Authority Security",
            "Capability Security",
            "Policy Security",
            "Execution Security",
            "Verification Security",
            "Proof Integrity",
            "Recovery",
          ].map((item, index) => (
            <div key={item}>
              <span>0{index + 1}</span>
              <strong>{item}</strong>
              <i>↓</i>
            </div>
          ))}
        </div>
      </PageSection>
      <PageSection
        eyebrow="02 / THREAT MODEL"
        title="Threats become explicit controls"
      >
        <div className="threat-grid">
          {[
            "Prompt Injection",
            "Agent Hijacking",
            "Credential Theft",
            "Authority Escalation",
            "Capability Replay",
            "Policy Bypass",
            "Provider Misbehavior",
            "Verifier Attack",
            "Validator Attack",
            "Proof Forgery",
          ].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </PageSection>
      <PageSection
        eyebrow="03 / RECOVERY"
        title="A secure action is recoverable"
      >
        <DataCards
          items={[
            [
              "Revoke",
              "Stop a capability or delegation from being used again.",
            ],
            [
              "Reverse",
              "Undo an external action where the provider supports it.",
            ],
            [
              "Compensate",
              "Start a compensating path when reversal is impossible.",
            ],
            [
              "Restore",
              "Return the system and its records and proof chain to a known state.",
            ],
          ]}
        />
      </PageSection>
      <section className="statement-section">
        <div className="container">
          <strong>
            Secure Autonomy = Bounded Authority + Enforced Policy + Verifiable
            Execution + Recoverable Failure
          </strong>
        </div>
      </section>
    </>
  );
}

export function RoadmapPage() {
  const phases = [
    {
      period: "MONTH 0–6",
      title: "TRUST PRIMITIVE",
      capabilities: [
        "Identity",
        "KYA",
        "Intent Manifest",
        "Authority Profile",
        "Capability",
      ],
    },
    {
      period: "MONTH 6–12",
      title: "ACTION CONTROL",
      capabilities: [
        "Agent Trust Gateway",
        "Policy Engine",
        "Action Session",
        "Execution Control",
        "Recovery",
      ],
    },
    {
      period: "MONTH 12–18",
      title: "VERIFICATION NETWORK",
      capabilities: [
        "Action Receipts",
        "Cryptographic Proof",
        "Verifiers",
        "Validators",
        "Shared Commitments",
      ],
    },
    {
      period: "MONTH 18–24",
      title: "AGENT ECONOMY",
      capabilities: [
        "Capital-Bearing Agents",
        "Verification Market",
        "Validator Economics",
        "Token Utility",
        "Multi-Agent Economic Workflows",
      ],
    },
  ];
  return (
    <>
      <PageHero
        eyebrow="ROADMAP"
        title="A path from trust primitives to agent economy."
        body="This roadmap describes intended stages of the ORIQ protocol. Timelines and scope may evolve; no stage should be treated as live until ORIQ explicitly publishes that status."
        status="PLANNED DIRECTION"
        className="roadmap-page-hero"
      />
      <PageSection
        eyebrow="ORIQ ROADMAP"
        title="Four stages of trusted autonomy"
        className="roadmap-section"
      >
        <div className="roadmap-journey">
          <ol className="roadmap-stages">
            {phases.map(({ period, title, capabilities }, index) => (
              <li className="roadmap-stage" key={title}>
                <div className="roadmap-stage-head">
                  <span className="roadmap-stage-marker">{index + 1}</span>
                  <span className="roadmap-stage-period">{period}</span>
                </div>
                <h3>{title}</h3>
                <ul className="roadmap-capabilities">
                  {capabilities.map((capability) => (
                    <li key={capability}>{capability}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      </PageSection>
      <PageSection
        eyebrow="LONG-TERM DIRECTION"
        title="The protocol grows by making cryptographic proof portable"
      >
        <p className="body-narrow">
          As trust domains connect, the same action semantics can support
          enterprise workflows, federated verification, and eventually a shared
          environment for agents, providers, capital, services, and validators.
        </p>
      </PageSection>
    </>
  );
}

export function WhitepaperPage() {
  return (
    <>
      <PageHero
        eyebrow="ORIQ PROTOCOL WHITEPAPER"
        title="Trusted Agent Infrastructure for Autonomous Execution."
        body="The full protocol document is the source of truth for ORIQ's thesis, architecture, security model, network direction, and roadmap."
      />
      <section className="whitepaper-section">
        <div className="container whitepaper-layout">
          <aside className="paper-nav">
            <span className="eyebrow">CONTENTS</span>
            <ol>
              {whitepaperChapters.map((chapter, index) => (
                <li key={chapter}>
                  <a href={`#chapter-${index + 1}`}>
                    {String(index + 1).padStart(2, "0")} {chapter}
                  </a>
                </li>
              ))}
            </ol>
          </aside>
          <article className="paper-body">
            <div className="paper-actions">
              <a
                className={`button ${WHITEPAPER_URL ? "" : "is-disabled"}`}
                href={WHITEPAPER_URL || "#whitepaper-source"}
                target={WHITEPAPER_URL ? "_blank" : undefined}
                rel={WHITEPAPER_URL ? "noreferrer" : undefined}
              >
                Read Online <span aria-hidden="true">↗</span>
              </a>
              <a
                className={`button button-quiet ${WHITEPAPER_URL ? "" : "is-disabled"}`}
                href={WHITEPAPER_URL || "#whitepaper-source"}
                target={WHITEPAPER_URL ? "_blank" : undefined}
                rel={WHITEPAPER_URL ? "noreferrer" : undefined}
              >
                Download PDF <span aria-hidden="true">↓</span>
              </a>
            </div>
            <div className="paper-notice" id="whitepaper-source">
              <span className="status-dot" />
              The official whitepaper link will be connected when the source
              document is provided.
            </div>
            {whitepaperChapters.map((chapter, index) => (
              <section
                className="paper-chapter"
                id={`chapter-${index + 1}`}
                key={chapter}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h2>{chapter}</h2>
                <p>Official chapter content pending source publication.</p>
              </section>
            ))}
          </article>
        </div>
      </section>
    </>
  );
}

export function DevelopersPage() {
  return (
    <>
      <PageHero
        eyebrow="DEVELOPERS"
        title="Build with ORIQ."
        body="Developer infrastructure is under active development. Official documentation, SDKs, and integration details will be published when ready."
      />
      <PageSection title="Developer access is coming into focus">
        <LinkCards
          items={[
            [
              "Documentation",
              "Technical reference and integration guidance.",
              "/developers",
            ],
            [
              "Quickstart",
              "A grounded first path for building a controlled action.",
              "/developers",
            ],
            [
              "MCP Integration",
              "Connect existing agent tooling through a trust gateway.",
              "/developers",
            ],
            [
              "SDK",
              "Official packages will appear here after the interface is stable.",
              "/developers",
            ],
          ]}
        />
      </PageSection>
    </>
  );
}

export function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="ABOUT ORIQ"
        title="Building trust infrastructure for autonomous systems."
        body="ORIQ is building infrastructure for trusted autonomous execution — enabling AI agents to operate with bounded authority, verifiable actions, accountable outcomes, and recoverable failure."
      />
      <PageSection
        eyebrow="LONG-TERM PATH"
        title="From action control to shared trust"
      >
        <div className="about-path">
          <span>Trusted Agent Infrastructure</span>
          <i>↓</i>
          <span>Verification Network</span>
          <i>↓</i>
          <strong>Agent Economy</strong>
        </div>
      </PageSection>
      <PageSection eyebrow="BOUNDARY" title="What ORIQ is and is not">
        <DataCards
          items={[
            [
              "ORIQ is",
              "A trust and action-control layer for systems that can act.",
            ],
            [
              "ORIQ is not",
              "A wallet, payment network, identity protocol, generic AI safety platform, or promise that every agent is trustworthy.",
            ],
            [
              "Current focus",
              "High-risk tools and capital-bearing agent workflows where boundaries and proof matter.",
            ],
            [
              "Future direction",
              "A shared verification environment across independent trust domains.",
            ],
          ]}
        />
      </PageSection>
      <PageSection eyebrow="PRIVACY" title="Privacy" id="privacy">
        <p className="body-narrow">
          This brochure site does not store form submissions or create user
          accounts. Contact inquiries are composed in the visitor&apos;s mail
          client and addressed to info@oriqai.com.
        </p>
      </PageSection>
      <PageSection eyebrow="TERMS" title="Terms" id="terms">
        <p className="body-narrow">
          ORIQ website material describes product direction and protocol
          concepts. Roadmap, network, token, and developer references are not
          claims of live availability unless explicitly stated.
        </p>
      </PageSection>
      <PageSection title="Contact">
        <p className="body-narrow">
          For early access, architecture conversations, or research
          collaboration, email{" "}
          <a className="inline-link" href="mailto:info@oriqai.com">
            info@oriqai.com
          </a>
          .
        </p>
      </PageSection>
    </>
  );
}
